import React, { useState, useEffect } from 'react';
import { Users, Plus, X, Mail, Check } from 'lucide-react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { usersAPI } from '../lib/api';

/**
 * CC Selector Component
 * Allows selecting team members and/or adding custom email addresses for CC
 * 
 * Props:
 * - selectedEmails: Array of selected CC email addresses
 * - onChange: Callback when selection changes (receives array of emails)
 * - defaultEnabled: Whether CC is enabled by default (default: true)
 * - showTeamToggle: Show "Select all team" checkbox (default: true)
 * - compact: Use compact layout (default: false)
 */
const CcSelector = ({ 
  selectedEmails = [], 
  onChange, 
  defaultEnabled = true,
  showTeamToggle = true,
  compact = false 
}) => {
  const [isEnabled, setIsEnabled] = useState(defaultEnabled);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allTeamSelected, setAllTeamSelected] = useState(defaultEnabled);
  const [customEmail, setCustomEmail] = useState('');
  const [customEmails, setCustomEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch team members (admins) on mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await usersAPI.getAll();
        // Filter to only show admins with valid emails
        const admins = response.data.filter(u => 
          u.role === 'admin' && 
          u.email && 
          u.email.includes('@')
        );
        setTeamMembers(admins);
        
        // If default enabled, select all team members
        if (defaultEnabled && admins.length > 0) {
          const adminEmails = admins.map(a => a.email);
          onChange?.(adminEmails);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, []);

  // Handle team toggle
  const handleTeamToggle = (checked) => {
    setAllTeamSelected(checked);
    if (checked) {
      const teamEmails = teamMembers.map(m => m.email);
      const combined = [...new Set([...teamEmails, ...customEmails])];
      onChange?.(combined);
    } else {
      onChange?.([...customEmails]);
    }
  };

  // Handle individual team member toggle
  const handleMemberToggle = (email, checked) => {
    let newSelection;
    if (checked) {
      newSelection = [...new Set([...selectedEmails, email])];
    } else {
      newSelection = selectedEmails.filter(e => e !== email);
      setAllTeamSelected(false);
    }
    onChange?.(newSelection);
    
    // Check if all team members are now selected
    const teamEmails = teamMembers.map(m => m.email);
    const allSelected = teamEmails.every(e => newSelection.includes(e));
    setAllTeamSelected(allSelected);
  };

  // Add custom email
  const handleAddCustomEmail = () => {
    if (customEmail && customEmail.includes('@') && !selectedEmails.includes(customEmail)) {
      const newCustomEmails = [...customEmails, customEmail];
      setCustomEmails(newCustomEmails);
      onChange?.([...new Set([...selectedEmails, customEmail])]);
      setCustomEmail('');
    }
  };

  // Remove custom email
  const handleRemoveCustomEmail = (email) => {
    const newCustomEmails = customEmails.filter(e => e !== email);
    setCustomEmails(newCustomEmails);
    onChange?.(selectedEmails.filter(e => e !== email));
  };

  // Handle enable/disable toggle
  const handleEnableToggle = (checked) => {
    setIsEnabled(checked);
    if (!checked) {
      onChange?.([]);
    } else if (allTeamSelected) {
      const teamEmails = teamMembers.map(m => m.email);
      onChange?.([...new Set([...teamEmails, ...customEmails])]);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Users className="w-4 h-4" />
          Team in CC
        </Label>
        <div className="text-sm text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="cc-enabled"
            checked={isEnabled}
            onCheckedChange={handleEnableToggle}
          />
          <Label htmlFor="cc-enabled" className="text-sm font-medium cursor-pointer flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team in CC ({selectedEmails.length})
          </Label>
        </div>
        
        {isEnabled && selectedEmails.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {selectedEmails.slice(0, 3).map(email => (
              <Badge key={email} variant="secondary" className="text-xs">
                {email.split('@')[0]}
              </Badge>
            ))}
            {selectedEmails.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{selectedEmails.length - 3}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Users className="w-4 h-4" />
          Team in CC
        </Label>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="cc-main-toggle"
            checked={isEnabled}
            onCheckedChange={handleEnableToggle}
          />
          <Label htmlFor="cc-main-toggle" className="text-sm cursor-pointer">
            {isEnabled ? 'Aan' : 'Uit'}
          </Label>
        </div>
      </div>

      {isEnabled && (
        <>
          {/* Team toggle */}
          {showTeamToggle && teamMembers.length > 0 && (
            <div className="flex items-center space-x-2 pb-2 border-b">
              <Checkbox
                id="select-all-team"
                checked={allTeamSelected}
                onCheckedChange={handleTeamToggle}
              />
              <Label htmlFor="select-all-team" className="text-sm cursor-pointer">
                Selecteer heel team ({teamMembers.length})
              </Label>
            </div>
          )}

          {/* Team members list */}
          {teamMembers.length > 0 && (
            <div className="space-y-2">
              {teamMembers.map(member => (
                <div key={member.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`member-${member.id}`}
                    checked={selectedEmails.includes(member.email)}
                    onCheckedChange={(checked) => handleMemberToggle(member.email, checked)}
                  />
                  <Label htmlFor={`member-${member.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                    <span>{member.first_name} {member.last_name}</span>
                    <span className="text-muted-foreground text-xs">({member.email})</span>
                  </Label>
                </div>
              ))}
            </div>
          )}

          {/* Custom email input */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="text-xs text-muted-foreground">Extra email toevoegen</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email@voorbeeld.be"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomEmail())}
                className="h-8 text-sm"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleAddCustomEmail}
                disabled={!customEmail || !customEmail.includes('@')}
                className="h-8"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Custom emails list */}
          {customEmails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {customEmails.map(email => (
                <Badge key={email} variant="secondary" className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomEmail(email)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Summary */}
          {selectedEmails.length > 0 && (
            <div className="text-xs text-muted-foreground flex items-center gap-1 pt-2">
              <Check className="w-3 h-3 text-green-500" />
              {selectedEmails.length} ontvanger(s) in CC
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CcSelector;
